import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useNotifications } from "../notifications/NotificationsContext";
import {
  fetchNotifications,
  markUnviewed,
  routeForNotification,
  typeLabel,
  KNOWN_TYPES,
} from "../notifications/api";
import { NotificationRow } from "../components/NotificationBell";
import "../notifications.css";

/* ------------------------------------------------------------------ *
 * Notifications feed — /notifications
 *
 * GET /notifications with page/limit, a viewed/unviewed toggle and a type
 * filter. Rows can be opened (marks viewed + follows the subject), marked
 * read, or put back to unread. The badge in the top bar follows the
 * `unviewed_count` every write returns.
 * ------------------------------------------------------------------ */

const PAGE_SIZE = 25;
const FILTERS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "read", label: "Read" },
];

export function NotificationsPage() {
  const navigate = useNavigate();
  const notif = useNotifications() || {};
  const { version, businessId, unviewedCount } = notif;

  const [filter, setFilter] = useState("all");
  const [type, setType] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [seenTypes, setSeenTypes] = useState([]);

  // Filter changes start over from page 1.
  useEffect(() => {
    setPage(1);
  }, [filter, type, businessId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const viewed = filter === "unread" ? false : filter === "read" ? true : undefined;
    fetchNotifications({ viewed, type: type || undefined, page, limit: PAGE_SIZE })
      .then((d) => {
        if (cancelled) return;
        setData({ items: d.items || [], total: Number(d.total) || 0 });
        // Every list answer carries the whole-business count — keep the
        // badge honest without bumping `version` (that would refetch).
        if (typeof d.unviewed_count === "number" && notif.setCount) {
          notif.setCount(d.unviewed_count);
        }
        setSeenTypes((prev) => {
          const next = new Set(prev);
          (d.items || []).forEach((n) => n.type && next.add(n.type));
          return [...next];
        });
      })
      .catch((e) => {
        if (cancelled) return;
        setError((e && e.message) || "Could not load notifications");
        setData({ items: [], total: 0 });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, type, page, version, businessId]);

  const typeOptions = useMemo(() => {
    const all = new Set([...KNOWN_TYPES, ...seenTypes]);
    return [...all].sort();
  }, [seenTypes]);

  const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  const open = async (item) => {
    const to = notif.markViewed ? await notif.markViewed(item) : routeForNotification(item);
    if (to) navigate(to, { state: { notification: item } });
  };

  const markRead = (item) => notif.markViewed && notif.markViewed(item._id);

  const markUnread = (item) =>
    markUnviewed([item._id])
      .then((d) => notif.applyCount && notif.applyCount(d.unviewed_count))
      .catch(() => {});

  return (
    <div className="nf-page">
      <header className="nf-head">
        <div className="nf-head-text">
          <h1 className="nf-title">Notifications</h1>
          <p className="nf-lede">
            Everyone in the business sees the same feed. Opening an item marks it
            read for the whole team.
          </p>
        </div>
        <div className="nf-head-actions">
          <button
            type="button"
            className="rf-btn rf-btn-ghost"
            disabled={!unviewedCount}
            onClick={() => notif.markAll && notif.markAll()}
          >
            Mark all as read
          </button>
        </div>
      </header>

      <div className="nf-toolbar">
        <div className="rf-segmented">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={filter === f.key ? "is-active" : ""}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              {f.key === "unread" && unviewedCount ? ` (${unviewedCount})` : ""}
            </button>
          ))}
        </div>
        <select
          className="nf-select"
          value={type}
          onChange={(e) => setType(e.target.value)}
          aria-label="Filter by type"
        >
          <option value="">All types</option>
          {typeOptions.map((t) => (
            <option key={t} value={t}>
              {typeLabel(t)}
            </option>
          ))}
        </select>
      </div>

      {error && <div className="nf-error">{error}</div>}

      <div className="nf-feed">
        {loading && data.items.length === 0 ? (
          <div className="nf-empty">Loading…</div>
        ) : data.items.length === 0 ? (
          <div className="nf-empty">
            {filter === "unread" ? "No unread notifications." : "No notifications yet."}
          </div>
        ) : (
          data.items.map((item) => (
            <NotificationRow
              key={item._id}
              item={item}
              onOpen={open}
              showMeta
              extra={
                <>
                  {routeForNotification(item) && (
                    <button type="button" className="nf-link" onClick={() => open(item)}>
                      Open
                    </button>
                  )}
                  {item.viewed ? (
                    <button type="button" className="nf-link" onClick={() => markUnread(item)}>
                      Mark unread
                    </button>
                  ) : (
                    <button type="button" className="nf-link" onClick={() => markRead(item)}>
                      Mark read
                    </button>
                  )}
                </>
              }
            />
          ))
        )}
      </div>

      <div className="nf-pager">
        <span>
          {data.total === 0
            ? "0 items"
            : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, data.total)} of ${data.total}`}
        </span>
        <span className="rf-spacer" />
        <button
          type="button"
          className="rf-btn rf-btn-ghost"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          Previous
        </button>
        <span>
          Page {page} of {pages}
        </span>
        <button
          type="button"
          className="rf-btn rf-btn-ghost"
          disabled={page >= pages}
          onClick={() => setPage((p) => Math.min(pages, p + 1))}
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default NotificationsPage;
